import React from 'react';
import { SlidersHorizontal } from 'lucide-react';
import Card from '../UI/Card';
import Button from '../UI/Button';
import MultiSelectDropdown from '../UI/MultiSelectDropdown';
import StrategyToolsDropdown from '../Strategies/StrategyToolsDropdown';
import CollapsibleAccountInfo from './CollapsibleAccountInfo';
import CandlestickChart from './CandlestickChart';

const inputClasses = 'w-full h-9 px-3 bg-card border border-border rounded-lg text-foreground text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const SIZING_MODES = {
  stocks: [
    { value: 'fixed_shares',     label: 'Fixed Shares',      orderSizeLabel: 'Shares per Level' },
    { value: 'fixed_investment', label: 'Fixed Investment',   orderSizeLabel: 'Investment per Level ($)' },
    { value: 'martingale',       label: 'Martingale/Dynamic', orderSizeLabel: 'Initial Shares' },
  ],
  crypto: [
    { value: 'fixed_quantity',   label: 'Fixed Quantity',     orderSizeLabel: 'Quantity per Level' },
    { value: 'fixed_investment', label: 'Fixed Investment',   orderSizeLabel: 'Investment per Level ($)' },
    { value: 'martingale',       label: 'Martingale/Dynamic', orderSizeLabel: 'Initial Quantity' },
  ],
};

function MobileStrategyBuilder({
  selectedType,
  formData,
  symbolName,
  accountBalance,
  buffer,
  setBuffer,
  isEditMode,
  loading,
  providerOptions,
  tradingModeLocked,
  assetClassLocked,
  selectedProvider,
  paramColumns,
  enabledOptions,
  setEnabledOptions,
  previewOrders,
  previewLoading,
  boundsChangeHandler,
  handleInputChange,
  handleParameterChange,
  handleTypeSelect,
  handleSubmit,
  handleImportRequest,
  onNotifications,
  renderParamInput,
  InfoTip,
  navigate,
  strategyTypes,
}) {
  const isGrid = selectedType?.id === 'grid';

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3 pb-16">
      {/* 1. Collapsible Account Info */}
      <CollapsibleAccountInfo
        strategyType={selectedType.id}
        parameters={formData.parameters}
        accountBalance={accountBalance}
        buffer={buffer}
        onBufferChange={setBuffer}
      />

      {/* 2. Chart */}
      <div className="min-h-[300px] h-[40vh]">
        <CandlestickChart
          symbol={formData.parameters.symbol}
          symbolName={symbolName}
          upperBound={formData.parameters.upperBound}
          lowerBound={formData.parameters.lowerBound}
          gridLevels={isGrid ? formData.parameters.gridLevels : undefined}
          timeframe="1M"
          onBoundsChange={boundsChangeHandler}
          isMobile
        />
      </div>

      {/* 3. Configuration */}
      <Card title="Configuration" action={selectedType && (
        <StrategyToolsDropdown
          mode="builder"
          formData={formData}
          selectedType={selectedType}
          onImport={handleImportRequest}
          onNotifications={onNotifications}
        />
      )}>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Strategy Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={inputClasses}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Strategy Type * <InfoTip id="strategyType" />
              </label>
              <select
                value={selectedType.id}
                onChange={(e) => {
                  const type = strategyTypes.find((t) => t.id === e.target.value);
                  if (type) handleTypeSelect(type);
                }}
                className={inputClasses}
                disabled={isEditMode}
                required
              >
                {strategyTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Provider *
              </label>
              <select
                value={formData.provider}
                onChange={(e) => handleInputChange('provider', e.target.value)}
                className={inputClasses}
                required
              >
                {providerOptions.map((p) => {
                  const typeLabel = p.type === 'both' ? 'Stocks + Crypto'
                    : p.type === 'crypto' ? 'Crypto'
                    : 'Stocks';
                  return (
                    <option key={p.value} value={p.value}>
                      {p.label} ({typeLabel})
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Trading Mode *
            </label>
            <select
              value={formData.tradingMode}
              onChange={(e) => handleInputChange('tradingMode', e.target.value)}
              className={inputClasses}
              disabled={tradingModeLocked}
              required
            >
              <option value="paper">Paper Trading (Simulated)</option>
              <option value="live">Live Trading (Real Money)</option>
            </select>
            {tradingModeLocked && (
              <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                Set by broker environment
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* 4. Symbol & Sizing (paramColumns[0]) */}
      <Card title="Symbol & Sizing">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Asset Class *
            </label>
            <select
              value={formData.assetClass}
              onChange={(e) => handleInputChange('assetClass', e.target.value)}
              className={inputClasses}
              disabled={assetClassLocked}
              required
            >
              <option value="stocks">Stocks</option>
              <option value="crypto">Crypto</option>
            </select>
            {assetClassLocked && (
              <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                Set by provider ({selectedProvider?.type})
              </p>
            )}
          </div>
          {isGrid && (
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Sizing Mode * <InfoTip id="sizingMode" />
              </label>
              <select
                value={formData.parameters.sizingMode || 'fixed_shares'}
                onChange={(e) => handleParameterChange('sizingMode', e.target.value)}
                className={inputClasses}
                required
              >
                {(SIZING_MODES[formData.assetClass] || SIZING_MODES.stocks).map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          )}
          {paramColumns[0].map((param) => renderParamInput(param))}
          <div className="col-span-2">
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Order Execution
            </label>
            <select
              value={formData.parameters.orderExecution || 'adaptive'}
              onChange={(e) => handleParameterChange('orderExecution', e.target.value)}
              className={inputClasses}
            >
              <option value="adaptive">Adaptive (Orderbook-aware)</option>
              <option value="strict">Standard (Strict limit orders)</option>
            </select>
          </div>
        </div>
      </Card>

      {/* 5. Thresholds (paramColumns[1]) */}
      <Card title="Thresholds">
        <div className="grid grid-cols-2 gap-3">
          {paramColumns[1].map((param) => renderParamInput(param))}
          {isGrid && formData.parameters.sizingMode === 'martingale' && (
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Martingale Multiplier * <InfoTip id="martingaleMultiplier" />
              </label>
              <input
                type="number"
                value={formData.parameters.martingaleMultiplier ?? 2}
                onChange={(e) => handleParameterChange('martingaleMultiplier', parseFloat(e.target.value))}
                min={1.1}
                step={0.1}
                className={inputClasses}
                required
              />
            </div>
          )}
          {isGrid && (
            <>
              {renderParamInput(selectedType.parameters.find((p) => p.name === 'upperBound'))}
              {renderParamInput(selectedType.parameters.find((p) => p.name === 'lowerBound'))}
            </>
          )}
        </div>
      </Card>

      {/* 6. Limits (paramColumns[2]) */}
      <Card title="Limits">
        <div className="grid grid-cols-2 gap-3">
          {paramColumns[2].map((param) => {
            if (isGrid && (param.name === 'upperBound' || param.name === 'lowerBound')) return null;
            if (!isGrid && !param.required) return null;
            return renderParamInput(param);
          })}

          {/* MeanReversion / Momentum / MarketMaking: stop loss, take profit, execution mode */}
          {(selectedType?.id === 'mean_reversion' || selectedType?.id === 'momentum' || selectedType?.id === 'market_making') && (
            <>
              <div className="col-span-2">
                <MultiSelectDropdown
                  label="Options"
                  icon={SlidersHorizontal}
                  options={[
                    { value: 'executionMode', label: 'Execution Mode' },
                    ...(selectedType?.id === 'market_making' ? [
                      { value: 'spreadMode', label: 'Dynamic Spread (BB)' },
                      { value: 'boundsMode', label: 'Price Bounds' },
                    ] : []),
                    ...(selectedType?.id === 'mean_reversion' ? [
                      { value: 'adaptiveThreshold', label: 'Adaptive Threshold (ATR)' },
                    ] : []),
                    ...(selectedType?.id === 'momentum' ? [
                      { value: 'adaptiveRsi', label: 'Adaptive RSI (ATR)' },
                    ] : []),
                    { value: 'stopLoss', label: 'Stop Loss' },
                    { value: 'takeProfit', label: 'Take Profit' },
                  ]}
                  selected={enabledOptions}
                  onChange={setEnabledOptions}
                />
              </div>
              {enabledOptions.includes('stopLoss') && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">
                      Stop Loss Mode <InfoTip id="stopLossMode" />
                    </label>
                    <select
                      value={formData.parameters.stopLossMode || 'none'}
                      onChange={(e) => handleParameterChange('stopLossMode', e.target.value)}
                      className={inputClasses}
                    >
                      <option value="none">None (Open-ended)</option>
                      <option value="percentage">Percentage</option>
                    </select>
                  </div>
                  {formData.parameters.stopLossMode === 'percentage' && (
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">
                        Stop Loss (%) * <InfoTip id="stopLossPercent" />
                      </label>
                      <input
                        type="number"
                        value={formData.parameters.stopLossPercent ?? 5}
                        onChange={(e) => handleParameterChange('stopLossPercent', parseFloat(e.target.value))}
                        min={0.1}
                        step={0.1}
                        className={inputClasses}
                        required
                      />
                    </div>
                  )}
                </>
              )}
              {enabledOptions.includes('takeProfit') && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">
                      Take Profit Mode <InfoTip id="takeProfitMode" />
                    </label>
                    <select
                      value={formData.parameters.takeProfitMode || 'none'}
                      onChange={(e) => handleParameterChange('takeProfitMode', e.target.value)}
                      className={inputClasses}
                    >
                      <option value="none">None (Open-ended)</option>
                      <option value="percentage">Percentage</option>
                    </select>
                  </div>
                  {formData.parameters.takeProfitMode === 'percentage' && (
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">
                        Take Profit (%) * <InfoTip id="takeProfitPercent" />
                      </label>
                      <input
                        type="number"
                        value={formData.parameters.takeProfitPercent ?? 3}
                        onChange={(e) => handleParameterChange('takeProfitPercent', parseFloat(e.target.value))}
                        min={0.1}
                        step={0.1}
                        className={inputClasses}
                        required
                      />
                    </div>
                  )}
                </>
              )}
              {enabledOptions.includes('executionMode') && (
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Execution Mode * <InfoTip id="executionMode" />
                  </label>
                  <select
                    value={formData.parameters.executionMode || 'auto-executor'}
                    onChange={(e) => handleParameterChange('executionMode', e.target.value)}
                    className={inputClasses}
                    required
                  >
                    <option value="auto-executor">Auto-Executor (Continuous)</option>
                  </select>
                </div>
              )}
              {enabledOptions.includes('executionMode') && (
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Order Type * <InfoTip id="orderType" />
                  </label>
                  <select
                    value={formData.parameters.orderType || 'limit'}
                    onChange={(e) => handleParameterChange('orderType', e.target.value)}
                    className={inputClasses}
                    required
                  >
                    <option value="limit">Limit Orders</option>
                    <option value="market">Market Orders</option>
                  </select>
                </div>
              )}
              {/* Market Making: Spread Mode */}
              {selectedType?.id === 'market_making' && enabledOptions.includes('spreadMode') && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">
                      Spread Mode <InfoTip id="spreadMode" />
                    </label>
                    <select
                      value={formData.parameters.spreadMode || 'fixed'}
                      onChange={(e) => handleParameterChange('spreadMode', e.target.value)}
                      className={inputClasses}
                    >
                      <option value="fixed">Fixed</option>
                      <option value="dynamic">Dynamic (Bollinger Bands)</option>
                    </select>
                  </div>
                  {formData.parameters.spreadMode === 'dynamic' && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          BB Period <InfoTip id="bbPeriod" />
                        </label>
                        <input
                          type="number"
                          value={formData.parameters.bbPeriod ?? 20}
                          onChange={(e) => handleParameterChange('bbPeriod', parseInt(e.target.value, 10))}
                          min={2}
                          max={200}
                          className={inputClasses}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          BB Std Dev <InfoTip id="bbStdDev" />
                        </label>
                        <input
                          type="number"
                          value={formData.parameters.bbStdDev ?? 2}
                          onChange={(e) => handleParameterChange('bbStdDev', parseFloat(e.target.value))}
                          min={0.5}
                          max={5}
                          step={0.1}
                          className={inputClasses}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
              {/* Market Making: Bounds Mode */}
              {selectedType?.id === 'market_making' && enabledOptions.includes('boundsMode') && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">
                      Bounds Mode <InfoTip id="boundsMode" />
                    </label>
                    <select
                      value={formData.parameters.boundsMode || 'none'}
                      onChange={(e) => handleParameterChange('boundsMode', e.target.value)}
                      className={inputClasses}
                    >
                      <option value="none">None</option>
                      <option value="fixed">Fixed (Manual)</option>
                      <option value="dynamic">Dynamic (Bollinger Bands)</option>
                    </select>
                  </div>
                  {formData.parameters.boundsMode === 'fixed' && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          Upper Bound ($)
                        </label>
                        <input
                          type="number"
                          value={formData.parameters.upperBound ?? ''}
                          onChange={(e) => handleParameterChange('upperBound', parseFloat(e.target.value))}
                          step={0.01}
                          className={inputClasses}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          Lower Bound ($)
                        </label>
                        <input
                          type="number"
                          value={formData.parameters.lowerBound ?? ''}
                          onChange={(e) => handleParameterChange('lowerBound', parseFloat(e.target.value))}
                          step={0.01}
                          className={inputClasses}
                        />
                      </div>
                    </>
                  )}
                  {formData.parameters.boundsMode === 'dynamic' && formData.parameters.spreadMode !== 'dynamic' && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          BB Period <InfoTip id="bbPeriod" />
                        </label>
                        <input
                          type="number"
                          value={formData.parameters.bbPeriod ?? 20}
                          onChange={(e) => handleParameterChange('bbPeriod', parseInt(e.target.value, 10))}
                          min={2}
                          max={200}
                          className={inputClasses}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          BB Std Dev <InfoTip id="bbStdDev" />
                        </label>
                        <input
                          type="number"
                          value={formData.parameters.bbStdDev ?? 2}
                          onChange={(e) => handleParameterChange('bbStdDev', parseFloat(e.target.value))}
                          min={0.5}
                          max={5}
                          step={0.1}
                          className={inputClasses}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
              {/* Mean Reversion: Adaptive Threshold */}
              {selectedType?.id === 'mean_reversion' && enabledOptions.includes('adaptiveThreshold') && (
                <>
                  <div className="col-span-2 flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="adaptiveThreshold-mobile"
                      checked={formData.parameters.adaptiveThreshold || false}
                      onChange={(e) => handleParameterChange('adaptiveThreshold', e.target.checked)}
                      className="h-4 w-4 rounded border-border bg-card text-primary focus:ring-ring"
                    />
                    <label htmlFor="adaptiveThreshold-mobile" className="text-xs font-medium text-muted-foreground">
                      Adaptive Threshold <InfoTip id="adaptiveThreshold" />
                    </label>
                  </div>
                  {formData.parameters.adaptiveThreshold && (
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">
                        Baseline ATR (%) <InfoTip id="baselineAtrPercent" />
                      </label>
                      <input
                        type="number"
                        value={formData.parameters.baselineAtrPercent ?? 1.5}
                        onChange={(e) => handleParameterChange('baselineAtrPercent', parseFloat(e.target.value))}
                        min={0.1}
                        max={10}
                        step={0.1}
                        className={inputClasses}
                      />
                    </div>
                  )}
                </>
              )}
              {/* Momentum: Adaptive RSI */}
              {selectedType?.id === 'momentum' && enabledOptions.includes('adaptiveRsi') && (
                <>
                  <div className="col-span-2 flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="adaptiveRsi-mobile"
                      checked={formData.parameters.adaptiveRsi || false}
                      onChange={(e) => handleParameterChange('adaptiveRsi', e.target.checked)}
                      className="h-4 w-4 rounded border-border bg-card text-primary focus:ring-ring"
                    />
                    <label htmlFor="adaptiveRsi-mobile" className="text-xs font-medium text-muted-foreground">
                      Adaptive RSI <InfoTip id="adaptiveRsi" />
                    </label>
                  </div>
                  {formData.parameters.adaptiveRsi && (
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">
                        Baseline ATR (%) <InfoTip id="baselineAtrPercent" />
                      </label>
                      <input
                        type="number"
                        value={formData.parameters.baselineAtrPercent ?? 1.5}
                        onChange={(e) => handleParameterChange('baselineAtrPercent', parseFloat(e.target.value))}
                        min={0.1}
                        max={10}
                        step={0.1}
                        className={inputClasses}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Other non-grid types: optional params with MultiSelectDropdown */}
          {!isGrid && selectedType?.id !== 'mean_reversion' && selectedType?.id !== 'momentum' && selectedType?.id !== 'market_making' && (() => {
            const optionalParams = selectedType?.parameters.filter((p) => !p.required) || [];
            if (optionalParams.length === 0) return null;
            return (
              <>
                <div className="col-span-2">
                  <MultiSelectDropdown
                    label="Options"
                    icon={SlidersHorizontal}
                    options={optionalParams.map((p) => ({ value: p.name, label: p.label }))}
                    selected={enabledOptions}
                    onChange={setEnabledOptions}
                  />
                </div>
                {optionalParams.filter((p) => enabledOptions.includes(p.name)).map((param) => renderParamInput(param))}
              </>
            );
          })()}

          {/* Grid: stop loss, execution mode, buy depth */}
          {isGrid && (
            <>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Stop Loss Mode <InfoTip id="stopLossMode" />
                </label>
                <select
                  value={formData.parameters.stopLossMode || 'none'}
                  onChange={(e) => handleParameterChange('stopLossMode', e.target.value)}
                  className={inputClasses}
                >
                  <option value="none">None (Open-ended)</option>
                  <option value="percentage">Percentage</option>
                </select>
              </div>
              {formData.parameters.stopLossMode === 'percentage' && (
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Stop Loss (%) * <InfoTip id="stopLossPercent" />
                  </label>
                  <input
                    type="number"
                    value={formData.parameters.stopLossPercent ?? 5}
                    onChange={(e) => handleParameterChange('stopLossPercent', parseFloat(e.target.value))}
                    min={0.1}
                    step={0.1}
                    className={inputClasses}
                    required
                  />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Execution Mode * <InfoTip id="executionMode" />
                </label>
                <select
                  value={formData.parameters.executionMode || 'auto-executor'}
                  onChange={(e) => handleParameterChange('executionMode', e.target.value)}
                  className={inputClasses}
                  required
                >
                  <option value="auto-executor">Auto-Executor (Continuous)</option>
                  <option value="position-aware">Position-Aware (Startup only)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Order Type * <InfoTip id="orderType" />
                </label>
                <select
                  value={formData.parameters.orderType || 'limit'}
                  onChange={(e) => handleParameterChange('orderType', e.target.value)}
                  className={inputClasses}
                  required
                >
                  <option value="limit">Limit Orders</option>
                  <option value="market">Market Orders</option>
                </select>
              </div>
              {(formData.parameters.orderType || 'limit') !== 'market' && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">
                      Buy Depth Mode <InfoTip id="buyDepthMode" />
                    </label>
                    <select
                      value={formData.parameters.buyDepthMode || 'levels'}
                      onChange={(e) => handleParameterChange('buyDepthMode', e.target.value)}
                      className={inputClasses}
                    >
                      <option value="levels">Grid Levels</option>
                      <option value="percentage">Percentage</option>
                    </select>
                  </div>
                  {(formData.parameters.buyDepthMode || 'levels') === 'levels' ? (
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">
                        Buy Depth (N levels) <InfoTip id="buyDepthN" />
                      </label>
                      <input
                        type="number"
                        value={formData.parameters.buyDepthN ?? 2}
                        onChange={(e) => handleParameterChange('buyDepthN', parseInt(e.target.value, 10))}
                        min={1}
                        max={50}
                        className={inputClasses}
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">
                        Buy Depth (%) <InfoTip id="buyDepthPercent" />
                      </label>
                      <input
                        type="number"
                        value={formData.parameters.buyDepthPercent ?? 2}
                        onChange={(e) => handleParameterChange('buyDepthPercent', parseFloat(e.target.value))}
                        min={0.1}
                        max={20}
                        step={0.1}
                        className={inputClasses}
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">
                      Sell Depth Mode <InfoTip id="sellDepthMode" />
                    </label>
                    <select
                      value={formData.parameters.sellDepthMode || 'levels'}
                      onChange={(e) => handleParameterChange('sellDepthMode', e.target.value)}
                      className={inputClasses}
                    >
                      <option value="levels">Grid Levels</option>
                      <option value="percentage">Percentage</option>
                    </select>
                  </div>
                  {(formData.parameters.sellDepthMode || 'levels') === 'levels' ? (
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">
                        Sell Depth (N levels) <InfoTip id="sellDepthN" />
                      </label>
                      <input
                        type="number"
                        value={formData.parameters.sellDepthN ?? 2}
                        onChange={(e) => handleParameterChange('sellDepthN', parseInt(e.target.value, 10))}
                        min={1}
                        max={50}
                        className={inputClasses}
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">
                        Sell Depth (%) <InfoTip id="sellDepthPercent" />
                      </label>
                      <input
                        type="number"
                        value={formData.parameters.sellDepthPercent ?? 2}
                        onChange={(e) => handleParameterChange('sellDepthPercent', parseFloat(e.target.value))}
                        min={0.1}
                        max={20}
                        step={0.1}
                        className={inputClasses}
                      />
                    </div>
                  )}
                </>
              )}
              <div className="col-span-2">
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Auto-Rebalance <InfoTip id="rebalanceMode" />
                </label>
                <select
                  value={formData.parameters.rebalanceMode || 'off'}
                  onChange={(e) => {
                    const mode = e.target.value;
                    handleParameterChange('rebalanceMode', mode);
                    // autoRebalance synced in validateParameters on backend
                  }}
                  className={inputClasses}
                >
                  <option value="off">Off</option>
                  <option value="fixed">Fixed (Preserve Spacing)</option>
                  <option value="dynamic">Dynamic (BB/ATR Bands)</option>
                </select>
              </div>
              {formData.parameters.rebalanceMode && formData.parameters.rebalanceMode !== 'off' && (
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Cooldown (min) <InfoTip id="rebalanceCooldownMin" />
                  </label>
                  <input
                    type="number"
                    value={formData.parameters.rebalanceCooldownMin ?? 15}
                    onChange={(e) => handleParameterChange('rebalanceCooldownMin', parseInt(e.target.value, 10))}
                    min={1}
                    max={1440}
                    className={inputClasses}
                  />
                </div>
              )}
              {formData.parameters.rebalanceMode === 'dynamic' && (
                <div className="col-span-2 grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">
                      BB Period <InfoTip id="bbPeriod" />
                    </label>
                    <input
                      type="number"
                      value={formData.parameters.bbPeriod ?? 20}
                      onChange={(e) => handleParameterChange('bbPeriod', parseInt(e.target.value, 10))}
                      min={2}
                      max={200}
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">
                      BB Std Dev <InfoTip id="bbStdDev" />
                    </label>
                    <input
                      type="number"
                      value={formData.parameters.bbStdDev ?? 2}
                      onChange={(e) => handleParameterChange('bbStdDev', parseFloat(e.target.value))}
                      min={0.5}
                      max={5}
                      step={0.1}
                      className={inputClasses}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      {/* 7. Planned Orders */}
      <Card title={`Planned Orders${previewOrders.filter(o => !o.metadata?.simulated).length ? ` (${previewOrders.filter(o => !o.metadata?.simulated).length})` : ''}`}>
        <div className="space-y-1">
          {!isEditMode ? (
            <p className="text-xs text-muted-foreground">Save strategy to preview planned orders.</p>
          ) : previewLoading ? (
            <div className="text-center py-4 text-muted-foreground text-xs">Computing...</div>
          ) : previewOrders.length === 0 ? (
            <p className="text-xs text-muted-foreground">No planned orders.</p>
          ) : (
            <div className="max-h-[80vh] overflow-y-auto space-y-1">
              {previewOrders.filter(o => !o.metadata?.simulated).map((order, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between text-xs px-2 py-1 rounded ${
                    order.side === 'buy' ? 'bg-green-900/10' : 'bg-red-900/10'
                  }`}
                >
                  <span className={`font-bold ${order.side === 'buy' ? 'text-green-500' : 'text-red-500'}`}>
                    {order.side.toUpperCase()}
                  </span>
                  <span className="text-muted-foreground">{order.quantity}</span>
                  <span className="text-muted-foreground">
                    {order.limitPrice ? `$${order.limitPrice.toFixed(2)}` : 'MKT'}
                  </span>
                </div>
              ))}
              {previewOrders.some(o => o.metadata?.simulated) && (
                <>
                  <div className="text-[10px] text-muted-foreground/60 uppercase tracking-wider pt-1 pb-0.5 border-t border-border/40">Scenarios</div>
                  {previewOrders.filter(o => o.metadata?.simulated).map((order, i) => (
                    <div
                      key={`sim-${i}`}
                      className="flex flex-col text-xs px-2 py-1 rounded border-l-2 border-dashed border-amber-500/60 opacity-60 bg-muted/30"
                    >
                      <div className="flex items-center justify-between">
                        <span className={`font-bold italic ${order.side === 'buy' ? 'text-green-500' : 'text-red-500'}`}>
                          {order.side.toUpperCase()} <span className="text-amber-500 font-normal">(Scenario)</span>
                        </span>
                        <span className="text-muted-foreground">{order.quantity}</span>
                        <span className="text-muted-foreground">
                          {order.limitPrice ? `$${order.limitPrice.toFixed(2)}` : 'MKT'}
                        </span>
                      </div>
                      {order.metadata?.scenario && (
                        <span className="text-[10px] text-muted-foreground/70 mt-0.5">{order.metadata.scenario}</span>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* 8. Sticky bottom bar */}
      <div className="sticky bottom-0 bg-background/80 backdrop-blur-sm border-t border-border px-3 py-3 -mx-3 mt-1 flex items-center gap-3">
        <Button type="submit" loading={loading} className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white">
          {loading
            ? (isEditMode ? 'Saving...' : 'Creating...')
            : (isEditMode ? 'Save Changes' : 'Create Strategy')}
        </Button>
        <Button type="button" variant="ghost" onClick={() => navigate('/strategies/manager')}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export default MobileStrategyBuilder;
